import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { updateEntityStatus } from 'features/sendouts/services/entityStatus';
import { EntityRoutes } from 'routes/constants';
import { TabKeys } from 'UI/constants/entityTypes';

import { DRAWER_TYPES } from '../components';
import { SUBMIT_CONFIG } from '../components/DrawerStack/DrawerStack.utils';
import { PROCESS_TYPE } from '../constants/constants';

export const useSendoutsSubmitCoordinator = ({ onSubmitSuccess, closeDrawer, clearStack }) => {
  const dispatch = useDispatch();
  const [submittingKey, setSubmittingKey] = useState(null);

  const finalizeSuccessfulSubmit = useCallback(
    async ({ closeOnSuccess = true, meta, normalizedResponse, payload }) => {
      const effects = normalizedResponse.effects || {};

      try {
        await onSubmitSuccess({ effects, meta, payload, response: normalizedResponse });
        await meta?.props?.onCustomSubmitSuccess?.();
      } catch (error) {
        // Un refresco fallido no invalida un guardado exitoso: el drawer se cierra igual.
      } finally {
        if (closeOnSuccess) {
          closeDrawer(meta.key);

          if (payload?.closeAll) {
            clearStack();
          }
        }
      }

      return normalizedResponse;
    },
    [clearStack, closeDrawer, onSubmitSuccess]
  );

  const setSendoverActiveStatus = useCallback(
    async ({ refreshData, sendoverId, status }) => {
      const meta = {
        type: DRAWER_TYPES.SendoverDetails,
        props: {
          sendoverId,
          onCustomSubmitSuccess: refreshData
        }
      };
      const payload = {
        payload: {
          status_id: status?.id
        }
      };

      const response = await updateEntityStatus({
        id: sendoverId,
        payload: payload.payload,
        processType: PROCESS_TYPE.Sendover.title
      });
      const normalizedResponse = {
        success: true,
        ...response,
        effects: {
          refreshTable: true
        }
      };

      if (normalizedResponse.alert) {
        dispatch(showAlert(normalizedResponse.alert));
      }

      if (!normalizedResponse.success) {
        return normalizedResponse;
      }

      return finalizeSuccessfulSubmit({
        closeOnSuccess: false,
        meta,
        normalizedResponse,
        payload
      });
    },
    [dispatch, finalizeSuccessfulSubmit]
  );

  const onPlacementCreated = useCallback(
    async ({ jobOrderId } = {}) => {
      const meta = { type: DRAWER_TYPES.CreatePlacement, props: {} };
      const normalizedResponse = { success: true, effects: { refreshTable: true } };

      await finalizeSuccessfulSubmit({
        closeOnSuccess: false,
        meta,
        normalizedResponse,
        payload: {}
      });
      clearStack();

      if (!jobOrderId) return;

      // Full-page navigation on purpose: the profile tabs read `?tab` only once on mount, so a
      // soft push would not switch tabs when the user is already on this job order's profile.
      const profilePath = EntityRoutes.JobOrderProfile.replace(':id', jobOrderId);
      window.location.assign(`${profilePath}?tab=${TabKeys.Placements}`);
    },
    [clearStack, finalizeSuccessfulSubmit]
  );

  const onDrawerSaved = useCallback(
    async (meta, payload) => {
      const handlerConfig = SUBMIT_CONFIG[meta.type];
      if (!handlerConfig?.handler) {
        throw new Error(`Missing submit handler for drawer type: ${meta.type}`);
      }

      setSubmittingKey(meta.key);
      try {
        const response = await handlerConfig.handler({ meta, payload });
        const normalizedResponse = { success: true, ...response };

        if (normalizedResponse.alert) {
          dispatch(showAlert(normalizedResponse.alert));
        }

        if (!normalizedResponse.success) {
          return normalizedResponse;
        }

        return await finalizeSuccessfulSubmit({ meta, normalizedResponse, payload });
      } finally {
        setSubmittingKey(current => (current === meta.key ? null : current));
      }
    },
    [dispatch, finalizeSuccessfulSubmit]
  );

  const onDrawerClose = useCallback(
    key => {
      closeDrawer(key);
    },
    [closeDrawer]
  );

  return {
    setSendoverActiveStatus,
    onPlacementCreated,
    onDrawerSaved,
    onDrawerClose,
    submittingKey
  };
};
